import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip as MuiTooltip, TextField, Checkbox, ListItemText, Chip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DateTimePicker } from '@material-ui/pickers';
import format from 'date-fns/format';
import { sub } from 'date-fns';
import MeasurementTableService from 'app/admin/measurement/service/MeasurementTableService';
import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import { TbWaveSine } from 'react-icons/tb';
import { PiSteps } from 'react-icons/pi';

const intervalButtons = [
    { label: 'All', unit: null },
    { label: '1y', unit: { years: 1 } },
    { label: '1m', unit: { months: 1 } },
    { label: '1w', unit: { weeks: 1 } },
    { label: '1d', unit: { days: 1 } }
];

export default function ChartPopup(props) {
    const [keys, setKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [lineType, setLineType] = useState('monotone');

    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const [valueMap, setValueMap] = useState({}); 
    const [externalIds, setExternalIds] = useState([]);

    const mainTheme = useSelector(selectMainTheme);

    const [loading, setLoading] = useState(false);
    const requestSeqRef = React.useRef(0);

    const labelToExternalId = React.useMemo(() => {
        const m = new Map();
        Object.entries(valueMap).forEach(([extId, label]) => m.set(label, extId));
        return m;
    }, [valueMap]);

    const selectedExternalIds = React.useMemo(
        () => selectedKeys
            .map(k => labelToExternalId.get(k))
            .filter(Boolean),
        [selectedKeys, labelToExternalId]
    );

    useEffect(() => {
        const id = props.item?.id;
        if (!id) return;

        MeasurementTableService.getTimeseriesDetails(id).then(details => {
            const valueMapping = {};
            const extIds = [];
            const keyLabels = [];

            details.sensors.forEach(sensor => {
            sensor.valueList.forEach(val => {
                const label = `${sensor.name}.${val.name}`;
                valueMapping[val.externalId] = label;
                extIds.push(val.externalId);
                keyLabels.push(label);
            });
            });

            setValueMap(valueMapping);
            setExternalIds(extIds);
            setKeys(keyLabels.sort());

            const start = details?.ts?.start ? new Date(details.ts.start) : null;
            const end   = details?.ts?.end   ? new Date(details.ts.end)   : null;
            if (start && end) {
                setMinDate(start);
                setMaxDate(end);
                setDateFrom(start);
                setDateTo(end);
            }
        });
    }, [props.item]);

    useEffect(() => {
        const id = props.item?.id;
        if (!id) return;
        if (!dateFrom || !dateTo) return;
        if (!selectedExternalIds.length) { 
            setFilteredData([]); 
            return; 
        }

        requestSeqRef.current += 1;
        const mySeq = requestSeqRef.current;
        setLoading(true);

        const payloadRange = { start: dateFrom.toISOString(), end: dateTo.toISOString() };

        MeasurementTableService
            .getData(id, payloadRange, selectedExternalIds)
            .then(raw => {
                if (mySeq !== requestSeqRef.current) return;

                const merged = {};
                const normalizeTimestamp = (ts) =>
                    new Date(new Date(ts).setMilliseconds(0)).toISOString();

                raw?.forEach(row => {
                    const ts = row.timestamp ?? row.ts;
                    const keyLabel = valueMap[row.valueId];
                    if (!keyLabel) return;

                    const nts = normalizeTimestamp(ts);
                    if (!merged[nts]) merged[nts] = { ts: nts };

                    merged[nts][keyLabel] = row.value;

                    if (row.outlier) {
                    merged[nts].outlier = true;
                    merged[nts].outlierDetails = row.outlierDetails;
                    }
                });

                const result = Object.values(merged)
                    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

                setFilteredData(result);
            })
            .catch(() => {
                if (mySeq !== requestSeqRef.current) return;
                setFilteredData([]);
            })
            .finally(() => {
                if (mySeq !== requestSeqRef.current) return;
                setLoading(false);
            });
    }, [props.item?.id, dateFrom, dateTo, selectedExternalIds, valueMap]);

    useEffect(() => {
        if (keys.length > 0 && selectedKeys.length === 0)
            setSelectedKeys([...keys]);
    }, [keys]);

    useEffect(() => {
        if (minDate && maxDate) {
            setDateFrom(minDate);
            setDateTo(maxDate);
        }
    }, [minDate, maxDate]);

    const isDataAvailable = () => keys.length > 0 && minDate && maxDate;

    const handleIntervalSelect = (unit) => {
        if (!unit) {
            setDateFrom(minDate);
            return;
        }
        const newFrom = sub(maxDate, unit);
        if (newFrom < minDate) return;
        setDateFrom(newFrom);
    };

    const isIntervalAvailable = (unit) => {
        if (!minDate || !maxDate) return false;
        if (!unit) return true;
        return sub(maxDate, unit) >= minDate;
    };

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Last ' : '') + label} arrow>
                    <span>
                        <IconButton size='small' onClick={() => handleIntervalSelect(unit)} disabled={!isIntervalAvailable(unit)}>
                            <span className='text-xs font-semibold p-4'>{label}</span>
                        </IconButton>
                    </span>
                </MuiTooltip>
            ))}
            <MuiTooltip title='Toggle line type' arrow>
                <span>
                    <IconButton size='small' onClick={() => setLineType(prev => prev === 'monotone' ? 'step' : 'monotone')}>
                        {lineType === 'monotone' ? <PiSteps size={20} /> : <TbWaveSine size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>
        </div>
    );

    const CustomizedDot = React.memo(({ cx, cy, payload }) => {
        if (payload.outlier) return <circle cx={cx} cy={cy} r={5} fill='red' stroke='white' strokeWidth={1.5} />;
        return null;
    });

    const HoverDot = React.memo(({ cx, cy }) => (
        <circle cx={cx} cy={cy} r={4} fill='#252f3e' stroke='white' strokeWidth={1.5} />
    ));
    
    const MIN_SEPARATION = 36;
    const COLOR_CAPACITY = Math.floor(360 / MIN_SEPARATION)
    const SAT = 80;
    const LIGHT = 45;

    const DASH_PATTERNS = [
        '0',            // solid
        '6 4',          // dash
        '4 4',          // short dash
        '3 6',          // dot
        '6 4 3 4',      // dash and dot
    ];

    const PALETTE = Array.from({ length: COLOR_CAPACITY }, (_, i) => {
        const hue = Math.round(((i + 0.5) * 360) / COLOR_CAPACITY) % 360;
        const sat   = SAT   + (((i * 7) % 3) - 1) * 4;
        const light = LIGHT + (((i * 11) % 3) - 1) * 4;
        return `hsl(${hue}, ${sat}%, ${light}%)`;
    });

    const colorMapRef = React.createRef();
    colorMapRef.current = new Map();
    const usedHuesRef = React.createRef();
    usedHuesRef.current = [];

    const stylesByKey = React.useMemo(() => {
        const sorted = [...keys].sort((a, b) => a.localeCompare(b));
        const m = new Map();
        sorted.forEach((k, i) => {
            const color = PALETTE[i % COLOR_CAPACITY];
            const tier  = Math.floor(i / COLOR_CAPACITY) % DASH_PATTERNS.length;
            m.set(k, { stroke: color, dash: DASH_PATTERNS[tier] });
        });
        return m;
    }, [keys]);

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open fullWidth maxWidth={filteredData.length ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    {/* eslint-disable-next-line */}
                    <div className={'flex items-center justify-between gap-32 px-32 ' + (filteredData.length ? 'border-b pb-14' : '')}>
                        {/* eslint-disable-next-line */}
                        <div className='whitespace-nowrap'>{props.title || ('Timeseries' + (props.name ? (' data for ' + props.name) : ''))}</div>
                        <Button onClick={() => props.onClose()}>Close</Button>
                    </div>
                    {isDataAvailable() && <div className='w-full flex flex-col gap-8 shadow-inner px-32 pt-14'>
                        <div className='w-full flex flex-col gap-8 rounded-xl bg-grey-100 py-10 shadow px-16'>
                            <div className='flex items-center gap-16'>
                                <Autocomplete
                                    className='flex-1'
                                    multiple
                                    options={keys}
                                    value={selectedKeys}
                                    onChange={(e, newValue) => setSelectedKeys(newValue)}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option}
                                    renderOption={(option, { selected }) => {
                                        const parts = option.split('.');
                                        const parent = parts.length > 1 ? parts[0] : null;
                                        const child = parts.length > 1 ? parts[1] : option;
                                        return (
                                            <>
                                                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                                <ListItemText primary={parent ? (<span><span className='font-semibold opacity-60'>{parent}.</span>{child}</span>) : (option)} />
                                            </>
                                        );
                                    }}
                                    renderTags={(value, getTagProps) => value.map((option, index) => (<Chip label={option} {...getTagProps({ index })} size='small' style={{ padding: '5px', marginRight: 4, marginBottom: 4, fontSize: '12px' }} />))}
                                    renderInput={(params) => {
                                        const { InputProps: extractedInputProps, inputProps: extractedInnerInputProps, ...filteredParams } = params;

                                        return (
                                            <TextField
                                                {...filteredParams}
                                                variant="outlined"
                                                label="Search and select"
                                                placeholder="Select keys"
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{ ...extractedInputProps, style: { minHeight: 40 } }}
                                                // eslint-disable-next-line react/jsx-no-duplicate-props
                                                inputProps={{ ...extractedInnerInputProps, style: { padding: '10px 8px', fontSize: '13px', lineHeight: 1.5 } }}
                                            />
                                        );
                                    }}
                                />

                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                    if (selectedKeys.length === keys.length) {
                                        setSelectedKeys([]);
                                    } else {
                                        setSelectedKeys([...keys]); 
                                    }
                                    }}
                                >
                                    {selectedKeys.length === keys.length ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>

                            <div className='flex-1 flex items-center gap-16'>
                                <DatePick className='flex-1' label='From' value={dateFrom} minDate={minDate} maxDate={maxDate} onChange={(v) => setDateFrom(v)} />
                                <DatePick className='flex-1' label='To' value={dateTo} minDate={minDate} maxDate={maxDate} onChange={(v) => setDateTo(v)} />
                                <IntervalButtons />
                            </div>

                        </div>
                    </div>}
                </DialogTitle>
                <DialogContent className='w-full flex flex-col p-20' style={{ height: filteredData.length ? '520px' : 'auto', overflow: 'auto' }}>
                    {loading && <div className='text-center'>Loading data…</div>}
                    {!loading && filteredData.length === 0 && <div className='text-center'>No data for the selected filters</div>}
                    {filteredData.length > 0 && (
                        <div className='w-full flex-1' style={{ minHeight: 400 }}>
                            <ResponsiveContainer width='100%' height='100%'>
                                <LineChart data={filteredData} margin={{ top: 10, right: 16, bottom: 56, left: 12 }}>
                                    <CartesianGrid strokeDasharray='3 3' />
                                    <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.  HH:mm:ss')} tickCount={10} />
                                    <YAxis tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                    <Tooltip />
                                    <Legend
                                        verticalAlign="bottom"
                                        align="center"
                                        wrapperStyle={{ paddingTop: 8, maxWidth: '100%', whiteSpace: 'normal', lineHeight: '18px' }}
                                        height={48}
                                        content={() => (
                                            <div style={{ display:'flex', flexWrap:'wrap', gap:12, justifyContent:'center', paddingTop:8 }}>
                                            {selectedKeys.map(key => {
                                                const st = stylesByKey.get(key);
                                                return (
                                                <div key={key} style={{ display:'flex', alignItems:'center', gap:6 }}>
                                                    <svg width="26" height="12" aria-hidden>
                                                    <line x1="0" y1="6" x2="26" y2="6"
                                                            stroke={st.stroke}
                                                            strokeWidth="3"
                                                            strokeDasharray={st.dash} />
                                                    </svg>
                                                    <span style={{ fontSize:12 }}>{key}</span>
                                                </div>
                                                );
                                            })}
                                            </div>
                                        )}
                                    />

                                    {selectedKeys.map(key => {
                                        const st = stylesByKey.get(key);
                                        return (
                                            <Line
                                                key={key}
                                                type={lineType}
                                                dataKey={key}
                                                name={key}
                                                stroke={st.stroke}
                                                strokeDasharray={st.dash}
                                                strokeWidth={2}
                                                dot={<CustomizedDot />}
                                                activeDot={<HoverDot />}
                                                connectNulls
                                                isAnimationActive={false}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}

function DatePick(props) {
    return (
        <DateTimePicker
            className={props.className}
            label={props.label}
            margin='dense'
            value={props.value ?? undefined}
            onChange={e => props.onChange(e)}
            ampm={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            format='yyyy.MM.dd. HH:mm:ss'
            cancelLabel='Vissza'
            okLabel='Ok'
            clearLabel='Törlés'
            inputVariant='outlined'
            clearable
        />
    );
}